import { useEffect, useState } from "react";
import { Printer, X } from "lucide-react";
import { buildBadgePrintHtml, mergeQrPayload, openBadgePrintWindow } from "@/lib/badge-print";

/**
 * Full-screen preview of a resolved badge + actions to print (browser dialog).
 */
export default function BadgePrintModal({
  open,
  onClose,
  eventId,
  template,
  dataSources,
  subjectType = "attendee",
  subjectId,
  eventTitle = "",
  footnote = "",
}) {
  const [previewSrcDoc, setPreviewSrcDoc] = useState("");
  const [mergedSources, setMergedSources] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !template || !dataSources) {
      setPreviewSrcDoc("");
      setMergedSources(null);
      setErr("");
      return;
    }
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr("");
      try {
        const merged = await mergeQrPayload(eventId, dataSources, subjectType, subjectId);
        if (cancelled) return;
        setMergedSources(merged);
        const html = await buildBadgePrintHtml({
          template,
          dataSources: merged,
          eventTitle,
          footnote,
          forModalPreview: true,
        });
        if (!cancelled) setPreviewSrcDoc(html);
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e || "Could not build preview"));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId, template, dataSources, subjectType, subjectId, eventTitle, footnote]);

  const handlePrint = async () => {
    if (!template || !mergedSources) return;
    const html = await buildBadgePrintHtml({
      template,
      dataSources: mergedSources,
      eventTitle,
      footnote,
      includePrintChrome: true,
    });
    const ok = openBadgePrintWindow(html);
    if (!ok) {
      /* eslint-disable no-alert */
      window.alert("Pop-up blocked. Allow pop-ups to print the badge.");
    }
  };

  const handlePrintFromIframe = () => {
    const el = document.getElementById("badge-print-preview-frame");
    if (el?.contentWindow) {
      el.contentWindow.focus();
      el.contentWindow.print();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col bg-black/80 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-print-title"
    >
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--border-default)] bg-[var(--bg-base)] shrink-0">
        <div>
          <div id="badge-print-title" className="font-heading text-lg text-[var(--text-primary)]">Badge preview</div>
          <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Check layout, then print (standard paper or badge stock).</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary text-sm" onClick={onClose} data-testid="badge-print-close">
            <X size={14} /> Close
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={handlePrintFromIframe}
            disabled={busy || !previewSrcDoc}
            title="Print only the badge area"
          >
            <Printer size={14} /> Print preview
          </button>
          <button type="button" className="btn-primary text-sm" onClick={handlePrint} disabled={busy || !mergedSources} data-testid="badge-print-dialog">
            <Printer size={14} /> Print (new window)
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-6">
        {err ? <p className="text-sm text-[var(--error)] mb-4">{err}</p> : null}
        {busy ? <p className="text-sm text-[var(--text-secondary)] mb-4">Generating preview…</p> : null}
        <iframe
          id="badge-print-preview-frame"
          title="Badge print preview"
          className="w-full max-w-4xl flex-1 min-h-[320px] rounded-xl border border-[var(--border-default)] bg-[var(--bg-elev)] shadow-2xl"
          srcDoc={previewSrcDoc || undefined}
        />
      </div>
    </div>
  );
}
